import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Lock, Loader2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, fmt, FREE_SHIPPING_THRESHOLD } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, clearCart, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    api
      .get("/payments/config")
      .then(({ data }) => setPaypalClientId(data.paypal_client_id))
      .catch(() => {});
  }, [isOpen]);

  const checkoutStripe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({
          slug: i.slug,
          size: i.size,
          color: i.color,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible de lancer le paiement. Réessaie.");
      setLoading(false);
    }
  };

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md bg-[#0F0F0F]/95 backdrop-blur-2xl border-l border-white/15 text-white p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <SheetTitle className="font-display uppercase tracking-widest text-2xl text-white">
            Panier <span className="text-ice">({items.length})</span>
          </SheetTitle>
          <SheetDescription className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            S'NO / FW26 Drop
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-4 text-center" data-testid="cart-empty-state">
              <p className="font-display uppercase text-xl text-zinc-500">Ton panier est vide</p>
              <button
                data-testid="cart-empty-shop-button"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/boutique");
                }}
                className="btn-ghost text-sm px-6 py-3"
              >
                Voir la boutique
              </button>
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-white/10">
              {items.map((item) => (
                <li key={item.key} className="py-4 flex gap-4" data-testid={`cart-item-${item.slug}`}>
                  <div className="w-20 h-24 shrink-0 overflow-hidden border border-white/10">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-display uppercase tracking-wide text-lg leading-tight">{item.name}</p>
                      <button
                        data-testid={`cart-remove-${item.slug}`}
                        onClick={() => removeItem(item.key)}
                        className="text-zinc-500 hover:text-red-400 transition-colors"
                        aria-label="Retirer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
                      {item.size} / {item.color}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/15">
                        <button
                          data-testid={`cart-qty-minus-${item.slug}`}
                          onClick={() => updateQty(item.key, -1)}
                          className="p-1.5 text-zinc-400 hover:text-ice"
                          aria-label="Moins"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${item.slug}`}>
                          {item.quantity}
                        </span>
                        <button
                          data-testid={`cart-qty-plus-${item.slug}`}
                          onClick={() => updateQty(item.key, 1)}
                          className="p-1.5 text-zinc-400 hover:text-ice"
                          aria-label="Plus"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="font-mono text-sm text-ice">{fmt(item.price * item.quantity)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5 space-y-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-2" data-testid="free-shipping-label">
                {remaining > 0
                  ? `Plus que ${fmt(remaining)} pour la livraison offerte`
                  : "Livraison offerte débloquée"}
              </p>
              <div className="h-1 bg-white/10 w-full">
                <div
                  className="h-full bg-ice transition-[width] duration-500"
                  style={{ width: `${progress}%` }}
                  data-testid="free-shipping-progress"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">Sous-total</span>
              <span className="font-display text-2xl text-white" data-testid="cart-subtotal">
                {fmt(subtotal)}
              </span>
            </div>

            <button
              data-testid="checkout-stripe-button"
              onClick={checkoutStripe}
              disabled={loading}
              className="btn-ice w-full disabled:opacity-60 disabled:pointer-events-none"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />}
              Paiement sécurisé — Stripe
            </button>

            {paypalClientId && (
              <div data-testid="checkout-paypal-section" className="pt-1">
                <PayPalScriptProvider
                  options={{ clientId: paypalClientId, currency: "EUR", intent: "capture" }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", color: "black", shape: "rect", label: "paypal", height: 45 }}
                    forceReRender={[subtotal]}
                    createOrder={async () => {
                      const { data } = await api.post("/paypal/create-order", {
                        items: items.map((i) => ({
                          slug: i.slug,
                          size: i.size,
                          color: i.color,
                          quantity: i.quantity,
                        })),
                      });
                      return data.paypal_order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await api.post("/paypal/capture-order", {
                          paypal_order_id: data.orderID,
                        });
                        clearCart();
                        setIsOpen(false);
                        navigate(`/payment/success?provider=paypal&order_id=${res.data.order_id}`);
                      } catch {
                        toast.error("Paiement PayPal échoué. Réessaie.");
                      }
                    }}
                    onError={() => toast.error("Erreur PayPal. Réessaie.")}
                  />
                </PayPalScriptProvider>
              </div>
            )}

            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 text-center">
              Taxes calculées au paiement / SSL sécurisé
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
